import { useState } from 'react';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { SectionLabel } from '@/shared/ui/SectionLabel';
import { useOverview, useDemoEnabled } from '../hooks/useOverview';
import type { OverviewStateId } from '../api/overviewApi';
import {
  DemoStateSwitcher,
  ZeroState,
  AttentionQueue,
  FindabilitySection,
  ReputationSection,
  MarketingSection,
  CompareSection,
} from '../components';
import styles from './OverviewPage.module.css';

export function OverviewPage() {
  const showDemoSwitcher = useDemoEnabled();
  const [demoOverride, setDemoOverride] = useState<OverviewStateId | undefined>(undefined);
  const { data, isLoading, isError } = useOverview(demoOverride);

  return (
    <div className={styles.page}>
      {showDemoSwitcher && <DemoStateSwitcher active={demoOverride} onChange={setDemoOverride} />}

      {isLoading || !data ? (
        <div className={styles.skeletonStack}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : isError ? (
        <div className={styles.errorCard}>
          Couldn't load the dashboard right now. Try refreshing the page.
        </div>
      ) : data.flags.isZero ? (
        <>
          {data.attention.length > 0 && (
            <section>
              <SectionLabel label="What needs my attention?" />
              <AttentionQueue items={data.attention} />
            </section>
          )}
          <ZeroState strategyInProgress={data.flags.strategyInProgress} />
        </>
      ) : (
        <>
          <section>
            <SectionLabel label="What needs my attention?" />
            <AttentionQueue items={data.attention} />
          </section>

          <section>
            <SectionLabel
              label="Are patients finding me online?"
              sub="Search clicks, AI search readiness, and your overall online visibility score"
            />
            <FindabilitySection flags={data.flags} data={data.findability} />
          </section>

          <section>
            <SectionLabel
              label="How do I look to patients?"
              sub="Reviews, ratings, and recent patient feedback"
            />
            <ReputationSection flags={data.flags} data={data.reputation} />
          </section>

          <section>
            <SectionLabel
              label="Is my marketing working?"
              sub="Total spend and conversions across connected ad platforms · conversions = form fills and calls as reported by Google / Meta"
            />
            <MarketingSection flags={data.flags} data={data.marketing} />
          </section>

          <section>
            <SectionLabel
              label="How do I compare?"
              sub="Nearby practice reviews (Google Places) · patient zip distribution and census demographics (ACS)"
            />
            <CompareSection flags={data.flags} data={data.compare} />
          </section>
        </>
      )}
    </div>
  );
}
